import MicroModal from 'micromodal';

import {trapFocus, removeTrapFocus} from '../../vendor/theme-scripts/theme-a11y';
import FetchError from '../../util/fetch-error';
import throttle from '../../util/throttle';
import {ImageCaption} from '../../features/image-caption';
import Media from '../../features/media';

const STATES = {
  INITIAL: 0,
  LOADING: 1,
};

const selectors = {
  quickviewHolder: 'data-quickview-holder',
  addButtonWrapper: '[data-add-action-wrapper]',
  modalContent: '[data-product-quickview-ajax]',
  quickviewModal: '[data-quickview-modal]',
  quickviewModalScrolls: '[data-drawer-scrolls]',
  quickviewModalTemplate: '[data-quickview-modal-template]',
  quickviewHead: '[data-quickview-head]',
  quickviewFormArea: '[data-quickview-form-area]',
  toggleButton: '[data-toggle-button]',
  media: '[data-media-slide]',
  mediaId: '[data-media-id]',
  focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
};

const classes = {
  hide: 'hide',
  active: 'is-active',
  expanded: 'is-expanded',
  loading: 'loading',
};

const visibleParts = {
  minPart: 250,
  mediumPart: 400,
};

/*
NOTE this is an initial crack at migrating existing product quick view modal logic
from WebComponents / direct DOM manipulation style to using Alpine. As such, it is
very rough and not at all (yet) idiomatic Alpine.
*/

function productQuickViewButton(_productId, handle) {
  return {
    state: STATES.INITIAL,

    // Selectors

    get quickviewHolder() {
      return this.$el.querySelector(`[${selectors.quickviewHolder}]`);
    },

    // Getters

    get isInitial() {
      return this.state === STATES.INITIAL;
    },

    get isLoading() {
      return this.state === STATES.LOADING;
    },

    get isDisabled() {
      return this.isLoading;
    },

    // Methods

    clickQuickviewButton(e) {
      if (this.modal && this.modalID) {
        // Reset modal ID before html content is loaded
        this.modal.id = this.modalID;
      }

      if (document.documentElement.hasAttribute('data-scroll-locked')) {
        this.scrollLockEnable = false;
      }

      this.state = STATES.LOADING;

      this.getQuickviewHTML();
    },

    getQuickviewHTML() {
      window
        .fetch(`${window.theme.routes.root_url}products/${handle}?section_id=api-product-quickview`)
        .then(this.handleErrors)
        .then((response) => {
          return response.text();
        })
        .then((response) => {
          const fresh = document.createElement('div');
          fresh.innerHTML = response;
          this.modalContent = this.modal.querySelector(selectors.modalContent);
          this.modalContent.innerHTML = fresh.querySelector('[data-api-content]').innerHTML;
          this.modalScroll = this.modal.querySelector(selectors.quickviewModalScrolls);
          const images = this.modalContent.querySelector(selectors.mediaId);

          if (images) {
            const imagesHolder = document.createElement('div');
            imagesHolder.innerHTML = images.parentElement.innerHTML;
            this.quickviewHolder.dispatchEvent(
              new CustomEvent('theme:quickview:media', {
                bubbles: true,
                detail: {
                  media: imagesHolder,
                },
              })
            );
          }

          const section = {
            container: this.modalContent,
          };

          this.media = new Media(section);

          this.modalCreate();
          this.state = STATES.INITIAL;
        });
    },

    modalCreate() {
      MicroModal.show(this.modalID, {
        onShow: (modal, el, event) => {
          this.quickviewHead = modal.querySelector(selectors.quickviewHead);
          this.quickviewFormArea = modal.querySelector(selectors.quickviewFormArea);
          this.toggleButton = modal.querySelector(selectors.toggleButton);

          this.toggleForm('loading');
          document.addEventListener(
            'theme:resize',
            throttle(() => {
              this.toggleForm('resize');
            }, 500)
          );

          this.clickEventToggleForm();

          const firstFocus = modal.querySelector(selectors.focusable);
          trapFocus(modal, {elementToFocus: firstFocus});
          this.modal.addEventListener('theme:quickview:close', this.modalClose);

          document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.modalScroll}));

          new ImageCaption(this.modal);
        },
        onClose: (modal, el, event) => {
          this.media.destroy();

          const allMedia = modal.querySelectorAll(selectors.media);

          allMedia.forEach((media) => {
            media.dispatchEvent(new CustomEvent('pause'));
          });

          removeTrapFocus();
          el.focus();
          this.modal.removeEventListener('theme:quickview:close', this.modalClose);
          if (this.scrollLockEnable) {
            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
          } else {
            this.scrollLockEnable = true;
          }
        },
      });
    },

    modalClose() {
      MicroModal.close(this.modalID);
    },

    handleErrors(response) {
      if (!response.ok) {
        return response.json().then(function (json) {
          const e = new FetchError({
            status: response.statusText,
            headers: response.headers,
            json: json,
          });
          throw e;
        });
      }
      return response;
    },

    toggleForm(event) {
      if (this.windowH === window.innerHeight && event === 'resize') {
        return;
      }

      if (!this.toggleButton.classList.contains(classes.hide)) {
        this.toggleButton.classList.add(classes.hide);
      }

      if (!this.quickviewFormArea.classList.contains(classes.expanded)) {
        this.quickviewFormArea.classList.add(classes.expanded);
      }

      if (event === 'resize') {
        this.quickviewFormArea.classList.add(classes.expanded);
        this.toggleButton.classList.add(classes.hide);
        this.windowH = window.innerHeight;
      }

      setTimeout(() => {
        const imagesVisiblePartOfImages = window.innerHeight - this.quickviewHead.offsetHeight - this.quickviewFormArea.offsetHeight;
        const hasMediumVisiblePart = imagesVisiblePartOfImages < visibleParts.mediumPart && imagesVisiblePartOfImages > visibleParts.minPart;
        const missingVisiblePart = imagesVisiblePartOfImages < visibleParts.minPart;

        if (hasMediumVisiblePart || missingVisiblePart) {
          this.toggleButton.classList.remove(classes.hide);
          this.toggleButton.classList.add(classes.active);
        } else {
          this.toggleButton.classList.add(classes.hide);
        }

        if (missingVisiblePart) {
          if (event === 'loading') {
            this.quickviewFormArea.classList.add(classes.loading);

            setTimeout(() => {
              this.quickviewFormArea.classList.remove(classes.loading);
            }, 50);
          }

          this.quickviewFormArea.classList.remove(classes.expanded);
          this.toggleButton.classList.remove(classes.active);
        } else {
          this.quickviewFormArea.classList.add(classes.expanded);
        }
      }, 200);
    },

    clickEventToggleForm() {
      if (!this.toggleButton) return;

      this.toggleButton.addEventListener('click', () => {
        this.toggleButton.classList.toggle(classes.active);
        this.quickviewFormArea.classList.toggle(classes.expanded);
      });
    },

    // Initialization

    init() {
      if (this.quickviewHolder) {
        this.modalTemplate = this.quickviewHolder.querySelector(selectors.quickviewModalTemplate);
        this.modal = document.querySelector(selectors.quickviewModal);
        this.modalID = this.quickviewHolder.getAttribute(selectors.quickviewHolder);
        this.modalContent = null;
        this.modalScroll = null;
        this.scrollLockEnable = true;
        this.windowH = window.innerHeight;

        if (this.modalTemplate && !this.modal) {
          const modalTemplateInner = this.modalTemplate.innerHTML;
          const htmlObject = document.createElement('div');
          htmlObject.innerHTML = modalTemplateInner;
          this.modalHtml = htmlObject.querySelector(selectors.quickviewModal);
          document.body.appendChild(this.modalHtml);
          this.modal = document.querySelector(selectors.quickviewModal);
        }
      }
    },
  };
}

export default productQuickViewButton;
